import { useEffect, useMemo, useState } from 'react';
import { useMeasure } from 'react-use';
import styled from '@emotion/styled';
import {
  addDays,
  eachDayOfInterval,
  startOfMonth,
  endOfMonth,
  getDay,
  setDay,
} from 'date-fns';
import { debounce } from 'lodash';
import { shadowLight } from 'app/colors';
import { borderRadii } from 'app/spacing';
import { Container, DateTitle, WeekdayHeaderCell } from './components/styled';
import { useTranslation } from 'react-i18next';
import { CALENDAR_VIEW_OPTIONS } from './constants';
import { getDateTitle } from './utils';

const DAYS_IN_WEEK = 7;
const MIN_CALENDAR_HEIGHT = 676;

const useRemeasureOnResize = (debounceValue = 100) => {
  const [remeasure, setRemeasure] = useState(false);

  useEffect(() => {
    const listener = debounce(() => {
      setRemeasure(true);
      setTimeout(() => setRemeasure(false));
    }, debounceValue);
    window.addEventListener('resize', listener);
    return () => window.removeEventListener('resize', listener);
  }, [debounceValue, setRemeasure]);

  return remeasure;
};

const useVisibleDates = (
  date,
  workingDaysOnly = false,
  skipWeekdays = [],
  skipLookup = {}
) =>
  useMemo(() => {
    const monthStart = startOfMonth(date);
    const monthEnd = endOfMonth(date);
    const start = addDays(monthStart, -getDay(monthStart));
    const end = addDays(monthEnd, 6 - getDay(monthEnd));
    const allDates = eachDayOfInterval({ start, end });

    if (!workingDaysOnly) {
      return allDates;
    }

    // Filter out non-working days
    return allDates.filter((d) => {
      const dateKey = d.toDateString();
      const weekday = d.getDay();
      const skipped = skipLookup[dateKey];
      const skippedByWeekday = skipWeekdays.includes(weekday);
      const isSkipped =
        skipped === true || (skipped !== false && skippedByWeekday);
      return !isSkipped;
    });
  }, [date, workingDaysOnly, skipWeekdays, skipLookup]);

const Grid = styled.div`
  ${shadowLight}
  border-radius: ${borderRadii.standard};
  display: grid;
  grid-template-columns: repeat(
    ${({ numColumns = DAYS_IN_WEEK }) => numColumns},
    minmax(calc(600px / ${({ numColumns = DAYS_IN_WEEK }) => numColumns}), 1fr)
  );
  grid-auto-rows: ${({ rowHeight }) => (rowHeight ? `${rowHeight}px` : '1fr')};
  height: calc(100% - 54px);
  // max-height is only necessary for Firefox where setting grid-auto-rows from 1fr to fixed pixels
  // causes the useMeasure hook to be called continually, increasing the row size by a small amount until
  // the container max-height is reached.
  max-height: ${({ rowHeight, numRows }) =>
    rowHeight ? `${rowHeight * numRows}px` : ''};
`;

const HeaderRow = styled.div`
  display: grid;
  grid-template-columns: repeat(
    ${({ numColumns = DAYS_IN_WEEK }) => numColumns},
    1fr
  );
  grid-template-rows: 1fr;
  width: 100%;
  height: 34px;
`;

const TopRowText = styled(DateTitle)`
  margin-top: 2px;
`;

const TopRow = ({ workingDaysOnly = false, skipWeekdays = [] }) => {
  const {
    i18n: { language },
  } = useTranslation();

  const weekdays = useMemo(() => {
    const allWeekdays = Array.from({ length: DAYS_IN_WEEK }, (_, i) => ({
      day: i,
      title: getDateTitle(
        setDay(new Date(), i),
        language,
        CALENDAR_VIEW_OPTIONS.month
      ),
    }));

    if (!workingDaysOnly) {
      return allWeekdays.map((w) => w.title);
    }

    // Filter to show only working days
    return allWeekdays
      .filter(({ day }) => !skipWeekdays.includes(day))
      .map((w) => w.title);
  }, [language, workingDaysOnly, skipWeekdays]);

  return (
    <HeaderRow numColumns={weekdays.length}>
      {weekdays.map((day) => (
        <WeekdayHeaderCell key={day}>
          <TopRowText size="buttonLabel">{day}</TopRowText>
        </WeekdayHeaderCell>
      ))}
    </HeaderRow>
  );
};

const MonthCells = ({ dates, calendarDate, Component, numColumns, ...rest }) =>
  dates.map((d, idx, arr) => (
    <Component
      key={d.getTime()}
      date={d}
      calendarMonth={calendarDate.getMonth()}
      calendarYear={calendarDate.getFullYear()}
      index={idx}
      total={arr.length}
      numColumns={numColumns}
      {...rest}
    />
  ));

/**
 * This separate component exists in order to contain the useMeasure hook. The parent component
 * (ExpandableCalendar) removes and reattaches this componnent on resize events so the initial
 * process of determining row width/height can be done. Specifically, setting initially grid-auto-rows
 * to "1fr" on initial load and then changing it to a fixed pixel value after useMeasure returns dimensions.
 */
const MeasuredGrid = ({
  dates,
  calendarDate,
  view,
  numRows,
  numColumns,
  Component,
}) => {
  const [ref, { height, width }] = useMeasure();

  // Safari does not measure the total grid height properly with `grid-auto-rows: 1fr`
  // This ternary condition is for handling Safari returning a height of 10 and forcing
  // the calendar to be it's minimum size. Alternatively, the grid could be wrapped in a
  // div with `display: grid` which causes the measurement to be accurate See:
  // https://stackoverflow.com/questions/55347359/why-is-css-grid-row-height-different-in-safari#answer-63197330
  const rowHeight =
    height > 0 && height < MIN_CALENDAR_HEIGHT / 2
      ? MIN_CALENDAR_HEIGHT / numRows
      : height / numRows;

  return (
    <Grid
      ref={ref}
      view={view}
      numRows={numRows}
      numColumns={numColumns}
      rowHeight={rowHeight}
    >
      <MonthCells
        dates={dates}
        calendarDate={calendarDate}
        Component={Component}
        numColumns={numColumns}
        rowWidth={width / numColumns}
        rowHeight={rowHeight}
      />
    </Grid>
  );
};

/**
 * Calendar component to view dates for a single month.
 *
 * @param props.date - the date for the calendar used to show the month
 * @param props.CellComponent - the cell component to be used when rendering each day. Defaults to {@link DefaultDateCell}
 * @param props.workingDaysOnly - if true, only show working days
 * @param props.skipWeekdays - array of weekday indices to skip (0-6, where 0 is Sunday)
 * @param props.skipLookup - object mapping date strings to skip status
 */
const Calendar = ({
  date,
  CellComponent,
  workingDaysOnly = false,
  skipWeekdays = [],
  skipLookup = {},
  ...rest
}) => {
  const visibleDates = useVisibleDates(
    date,
    workingDaysOnly,
    skipWeekdays,
    skipLookup
  );

  // Calculate number of columns based on working days
  const numColumns = useMemo(() => {
    if (!workingDaysOnly) {
      return DAYS_IN_WEEK;
    }
    return DAYS_IN_WEEK - skipWeekdays.length;
  }, [workingDaysOnly, skipWeekdays]);

  return (
    <Container {...rest}>
      <TopRow workingDaysOnly={workingDaysOnly} skipWeekdays={skipWeekdays} />
      <Grid numColumns={numColumns}>
        <MonthCells
          dates={visibleDates}
          calendarDate={date}
          Component={CellComponent}
          numColumns={numColumns}
        />
      </Grid>
    </Container>
  );
};

/**
 * Calendar component to be used when providing {@link ExpandableDateCell} as the `CellComponent` prop.
 * This component will measure the container grid's width and height and pass along the appropriate width
 * and height values to each cell.
 *
 * @remarks `grid-auto-rows` is initially set to 1fr so the heights of the cells can be dynamic based on the cell's
 * contents and the container element's rules. Once an automatic height has been obtained the rowHeight is calculated
 * based on the number of weeks in the month and explicitly set to that number of pixels so when a cell is expanded
 * it's larger size does not cause the entire row change with it. This is also why there are separate Grid wrapping components
 * for month and week/day views. We do not want to reuse the height of the other views when switching back to the month view.
 *
 * Note: Firefox will resize multiple times after setting `grid-auto-rows` to a pixel value. See comment in Grid about setting
 * of `max-height` value.
 *
 * @param props.date - the date for the calendar used to show the current month
 * @param props.CellComponent - the cell component to be used when rendering each day. Defaults to {@link DefaultDateCell}
 * @param props.workingDaysOnly - if true, only show working days
 * @param props.skipWeekdays - array of weekday indices to skip (0-6, where 0 is Sunday)
 * @param props.skipLookup - object mapping date strings to skip status
 */
export const ExpandableCalendar = ({
  date,
  CellComponent,
  workingDaysOnly = false,
  skipWeekdays = [],
  skipLookup = {},
  ...rest
}) => {
  const visibleDates = useVisibleDates(
    date,
    workingDaysOnly,
    skipWeekdays,
    skipLookup
  );
  const remeasure = useRemeasureOnResize(350);

  // Calculate number of columns based on working days
  const numColumns = useMemo(() => {
    if (!workingDaysOnly) {
      return DAYS_IN_WEEK;
    }
    return DAYS_IN_WEEK - skipWeekdays.length;
  }, [workingDaysOnly, skipWeekdays]);

  const numRows = Math.ceil(visibleDates.length / numColumns);

  return (
    <Container {...rest}>
      <TopRow workingDaysOnly={workingDaysOnly} skipWeekdays={skipWeekdays} />
      {remeasure && (
        <Grid numRows={numRows} numColumns={numColumns}>
          <MonthCells
            dates={visibleDates}
            calendarDate={date}
            Component={CellComponent}
            numColumns={numColumns}
            rowWidth={0}
            rowHeight={0}
          />
        </Grid>
      )}
      {!remeasure && (
        <MeasuredGrid
          numRows={numRows}
          numColumns={numColumns}
          dates={visibleDates}
          calendarDate={date}
          Component={CellComponent}
        />
      )}
    </Container>
  );
};

const MonthCalendar = ({ expandable = false, ...rest }) => {
  return expandable ? <ExpandableCalendar {...rest} /> : <Calendar {...rest} />;
};

export default MonthCalendar;
